import { useState, useEffect } from 'react';

import { Lock, Eye, EyeOff } from 'lucide-react';
import { FormInput } from '../components/FormInput';
import { motion } from 'framer-motion';

export const ResetPassword = ({ showNotification }) => {
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get('token');
  const themeParam = searchParams.get('theme');
  const themeColor = themeParam ? decodeURIComponent(themeParam) : '#0d0d0d';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      if (showNotification) showNotification('Invalid or missing reset token', 'error');
      localStorage.setItem('org.view', 'login');
      window.location.href = '/';
    }
  }, [token, showNotification]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      if (showNotification) showNotification('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      if (showNotification) showNotification('Password must be at least 6 characters');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (showNotification) showNotification(data.error || 'Failed to save password', 'error');
      } else {
        if (showNotification) showNotification('Password saved successfully!', 'success');
        setIsSuccess(true);
      }
    } catch {
      if (showNotification) showNotification('Failed to connect to server');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) return null;

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#faf6ec] p-4">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-white border border-[var(--theme-tertiary)] rounded-3xl p-6 sm:p-8 shadow-[0_30px_60px_-30px_rgba(15,23,42,0.25)]"
      >
        <div className="flex items-center justify-center gap-2 mb-1.5">
          <img
            src="/logo.png"
            alt="Mitra POS Logo"
            className="w-10 h-10 object-contain shrink-0 drop-shadow-md"
          />
          <p className="text-[14px] font-extrabold uppercase tracking-[0.2em] text-[#0d0d0d]">
            MITRA POS
          </p>
        </div>

        <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0d0d0d] text-center mt-4">
          {isSuccess ? 'Password Reset!' : 'Reset Password'}
        </h2>
        
        {isSuccess ? (
          <div className="mt-6 flex flex-col items-center justify-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: `${themeColor}1a`, color: themeColor }}>
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <p className="text-center text-gray-600 font-medium mb-6">
              Your password has been successfully updated. You will be redirected to the login page momentarily.
            </p>
            <button
              onClick={() => {
                localStorage.setItem('org.view', 'login');
                window.location.href = '/';
              }}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-[15px] font-bold text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all"
              style={{ background: themeColor, boxShadow: `0 14px 30px -10px ${themeColor}50` }}
            >
              Return to Login
            </button>
          </div>
        ) : (
          <>
            <p className="mt-1.5 text-sm text-slate-500 text-center mb-6">
              Enter your new password below.
            </p>

            <form onSubmit={submit} className="space-y-4">
              <FormInput
                label="New Password"
                icon={Lock}
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                rightIcon={showPassword ? EyeOff : Eye}
                onRightIconClick={() => setShowPassword((v) => !v)}
                autoComplete="new-password"
                required
              />

              <FormInput
                label="Confirm New Password"
                icon={Lock}
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                rightIcon={showConfirmPassword ? EyeOff : Eye}
                onRightIconClick={() => setShowConfirmPassword((v) => !v)}
                autoComplete="new-password"
                required
              />

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center gap-3 px-5 py-3.5 rounded-xl text-white font-bold text-sm transition-colors shadow-lg outline-none focus-visible:ring-4 min-w-0 hover:brightness-95 mt-4 disabled:opacity-50"
                style={{
                  background: themeColor,
                  boxShadow: `0 14px 30px -10px ${themeColor}50`,
                }}
              >
                {isSubmitting ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
};
